import { ChevronRight, Webhook } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import type { WebhookEndpoint } from "@/frontend/types/dashboard/settings/webhooks";
import { summarizeEvents } from "@/frontend/utils/webhooks";

/**
 * Renders a single webhook endpoint as a clickable row. The whole card
 * navigates to the detail view where deliveries, rotate, disable, and delete
 * live — mirroring the export DestinationCard pattern.
 *
 * @param endpoint - The webhook endpoint to display
 * @param onClick - Callback invoked when the card is clicked (navigates to detail view)
 */
export function WebhookCard({
  endpoint,
  onClick,
}: {
  endpoint: WebhookEndpoint;
  onClick: () => void;
}) {
  const isActive = endpoint.status === "active";

  return (
    <div
      onClick={onClick}
      className={cn(
        "group flex items-center gap-4 px-4 py-4 rounded-lg border transition-colors cursor-pointer hover:bg-muted/30",
      )}
    >
      <Webhook className="size-6 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "text-sm font-medium truncate min-w-0",
              !endpoint.description && "font-mono",
            )}
          >
            {endpoint.description || endpoint.url}
          </span>
          <span
            className={cn(
              "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize shrink-0",
              isActive
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
            )}
          >
            {isActive ? "Active" : "Disabled"}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {summarizeEvents(endpoint.events)}
          {endpoint.description && (
            <>
              {" · "}
              <span className="font-mono">{endpoint.url}</span>
            </>
          )}
        </div>
      </div>
      <ChevronRight className="size-4 text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5" />
    </div>
  );
}